import React, {useContext, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Constants} from "../common/Constants";
import {usePagePathSegment} from "../common/hooks/usePagePathSegment";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {AuthUtility} from "../common/AuthUtility";
import {ApiUtility} from "../common/ApiUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {GlobalContext} from "./AdminFrame";

interface AdminSidebarLinkProps {
    selected: boolean;
    iconSrc: string;
    label: string;
    path: string;
}

export let AdminSidebar = function () {
    let pagePathSegment = usePagePathSegment();
    let context = useContext(GlobalContext);
    let navigate = useNavigate();
    let [isExpanded, setIsExpanded] = useState(false);
    let canAccessSettings = AuthUtility.userHasPermissions([Constants.permissionNames.activityScopeUpdate]);

    function logout() {
        ApiUtility.logout()
            .then(function () {
                context.navigator.navigateTo('/login');
            })
            .catch(function (err) {
                console && console.error(err);
                MessageDisplayerUtility.error('An error occurred while logging out.');
                context.navigator.navigateTo('/login');
            });
    }

    return (
        <div className={`admin-frame-sidebar ${isExpanded ? 'expanded' : ''}`}>
            <div className="admin-frame-sidebar-content">
                <div className="admin-frame-sidebar-header">
                    <img
                        src="/static/images/berger-logo-full-light-gray.svg"
                        className="admin-frame-logo"
                    />
                    <img
                        src="/static/images/berger-logo-mark-light-gray.svg"
                        className="admin-frame-logo-mark"
                    />
                </div>
                <div className="admin-frame-sidebar-links-section">
                    <div className="admin-frame-sidebar-links-group">
                        <div className="admin-frame-sidebar-links-group-body">
                            <AdminSidebarLink
                                path={`/${Constants.routes.dashboard.pathSegment}`}
                                selected={pagePathSegment === Constants.routes.dashboard.pathSegment}
                                iconSrc={'/static/images/dashboard-icon-textcolor.svg'}
                                label={Constants.routes.dashboard.label}
                            />
                            <AdminSidebarLink
                                path={`/${Constants.routes.dispatch.pathSegment}`}
                                selected={pagePathSegment === Constants.routes.dispatch.pathSegment}
                                iconSrc={'/static/images/dispatch-icon-textcolor.svg'}
                                label={Constants.routes.dispatch.label}
                            />
                            <AdminSidebarLink
                                path={`/${Constants.routes.brokerReports.pathSegment}`}
                                selected={pagePathSegment === Constants.routes.brokerReports.pathSegment}
                                iconSrc={'/static/images/broker-report-icon-textcolor.svg'}
                                label={Constants.routes.brokerReports.label}
                            />
                        </div>
                    </div>
                    <div className="admin-frame-sidebar-links-group last">
                        <div className="admin-frame-sidebar-links-group-header">
                            Admin
                        </div>
                        <div className="admin-frame-sidebar-links-group-body">
                            <AdminSidebarLink
                                path={`/${Constants.routes.jobs.pathSegment}`}
                                selected={pagePathSegment === Constants.routes.jobs.pathSegment}
                                iconSrc={'/static/images/jobs-icon-textcolor.svg'}
                                label={Constants.routes.jobs.label}
                            />
                            <AdminSidebarLink
                                path={`/${Constants.routes.brokers.pathSegment}`}
                                selected={pagePathSegment === Constants.routes.brokers.pathSegment}
                                iconSrc={'/static/images/brokers-icon-textcolor.svg'}
                                label={Constants.routes.brokers.label}
                            />
                            <ConditionalRenderer
                                if={AuthUtility.userHasPermissions([Constants.permissionNames.userCreate])}
                                renderer={() => (
                                    <AdminSidebarLink
                                        path={`/${Constants.routes.users.pathSegment}`}
                                        selected={pagePathSegment === Constants.routes.users.pathSegment}
                                        iconSrc={'/static/images/users-icon-textcolor.svg'}
                                        label={Constants.routes.users.label}
                                    />
                                )}
                            />
                            {/*
                            todo later feature
                            <AdminSidebarLink
                                path={`/${Constants.routes.myAccount.pathSegment}`}
                                selected={pagePathSegment === Constants.routes.myAccount.pathSegment}
                                iconSrc={'/static/images/account-icon-textcolor.svg'}
                                label={Constants.routes.myAccount.label}
                            />
                            */}
                        </div>
                    </div>
                    <ConditionalRenderer
                        if={canAccessSettings}
                        renderer={() => (
                            <div
                                className={`admin-frame-sidebar-settings ${pagePathSegment === Constants.routes.settings.pathSegment ? 'selected' : ''}`}
                                onClick={() => navigate(`/${Constants.routes.settings.pathSegment}/activity-scopes`)}
                            >
                                <div className="admin-frame-sidebar-link-icon-box">
                                    <img
                                        className="admin-frame-sidebar-link-icon"
                                        src="/static/images/settings-icon-green.svg"
                                    />
                                </div>
                                <div className="admin-frame-sidebar-link-text">{Constants.routes.settings.label}</div>
                            </div>
                        )}
                    />
                    <div
                        className={`admin-frame-sidebar-logout ${canAccessSettings ? 'follows-settings' : ''}`}
                        onClick={logout}
                    >
                        <div className="admin-frame-sidebar-link-icon-box">
                            <img
                                className="admin-frame-sidebar-link-icon"
                                src="/static/images/logout-icon-green.svg"
                            />
                        </div>
                        <div className="admin-frame-sidebar-link-text">Logout</div>
                    </div>
                </div>
                <div className="admin-frame-sidebar-footer">
                    <div className="admin-frame-sidebar-footer-divider"></div>
                    <div className="admin-frame-sidebar-footer-text">Truck Dispatch</div>
                </div>
                <div
                    className="admin-frame-sidebar-expand-toggle"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <img
                        className="admin-frame-sidebar-expand-toggle-chevron expand"
                        src="/static/images/sidebar-expand-chevron-green.svg"
                    />
                    <img
                        className="admin-frame-sidebar-expand-toggle-chevron collapse"
                        src="/static/images/sidebar-collapse-chevron-green.svg"
                    />
                    <div className="admin-frame-sidebar-expand-toggle-label">COLLAPSE</div>
                </div>
            </div>
        </div>
    );
};

let AdminSidebarLink = function (props: AdminSidebarLinkProps) {
    let navigate = useNavigate();

    return (
        <div
            className={`admin-frame-sidebar-link ${props.selected ? 'selected' : ''}`}
            onClick={() => navigate(props.path)}
        >
            <div className="admin-frame-sidebar-link-content">
                <div className="admin-frame-sidebar-link-icon-box">
                    <img
                        className="admin-frame-sidebar-link-icon"
                        src={props.iconSrc}
                    />
                </div>
                <div className="admin-frame-sidebar-link-text">{props.label}</div>
            </div>
        </div>
    )
}
